/* eslint-disable @typescript-eslint/naming-convention */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { MedicalHistorySubmit } from '@brands/Dashboard/MedicalHistory/MedicalHistory/utils/types';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalHistory } from '@brands/services/clinical_data/types/types';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Container from '../../../../../Components/Container/Container';
import TextField from '../../../../../Components/Inputs/MuiTextField/TextField';
import { useMedicalHistoryOptional } from '@brands/Dashboard/PatientForms/PatientForm/utils/clinicalEnablementFlow';
import { sanitizeMedicalHistoryBooleans } from '../utils/medicalHistoryDefaults';
import styles from './styles.module.scss';

const ClinicalDataStepThree = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const { values, selectedPatient } = useAppSelector(selectPatientForm);
  const bypassRequired = useMedicalHistoryOptional(values);

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const { state, pathname } = useLocation();
  const isIntakeProcess = useMemo(() => {
    return state?.isIntakeProcess || pathname.includes('clinical-data') || false;
  }, [state]);

  const [loading, setLoading] = useState(false);
  const [historyObjectId, setHistoryObjectId] = useState<number | null>(null);
  const [conditionsTreated, setConditionsTreated] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Tracks whether the patient actually edited this step, so we only confirm a
  // real save instead of showing a misleading toast on plain navigation.
  const userEditedRef = useRef(false);

  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory>({
    id: 0,
    under_care_of_physician: null,
    conditions_treated: null,
    taking_medication: null,
    medications: null,
    allergic_to_medications: null,
    medications_allergies: null,
    using_substances: [],
    interested_in_stopping: null,
    height: '',
    weight: '',
    pharmacy_name: null,
    pharmacy_street_address: null,
    pharmacy_city: null,
    pharmacy_state: null,
    pharmacy_zip_code: null,
    pharmacy_phone_number: null,
  } as MedicalHistory);

  const saveFormData = useCallback((): void => {
    if (conditionsTreated && hasUnsavedChanges && !loading) {
      sessionStorage.setItem('conditionsTreated', conditionsTreated);
    }
  }, [hasUnsavedChanges, conditionsTreated, loading]);

  useEffect(() => {
    return () => {
      saveFormData();
    };
  }, [saveFormData]);

  useEffect(() => {
    // Handle page refresh/close
    const handleBeforeUnload = (): void => {
      saveFormData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveFormData]);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      setHasUnsavedChanges(false);

      const medicalHistoryPayload: MedicalHistorySubmit = {
        user_id: selectedPatient?.value as number,
        ...medicalHistory,
        under_care_of_physician: true,
        conditions_treated: conditionsTreated || '',
      };

      const sanitizedPayload = sanitizeMedicalHistoryBooleans(medicalHistoryPayload, bypassRequired);
      if (!historyObjectId) {
        await addClinicalData('medical_history', sanitizedPayload);
      } else {
        await updateClinicalData('medical_history', historyObjectId.toString(), sanitizedPayload);
      }

      if (userEditedRef.current && !isIntakeProcess) {
        showSnackbar('Medical history saved successfully!', 'success');
      }
      sessionStorage.removeItem('conditionsTreated');
      sessionStorage.removeItem('underCare');
      setLoading(false);
      roleNavigate('/clinical-data/step-four');
    } catch (error) {
      setHasUnsavedChanges(true);
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const savedDraft = sessionStorage.getItem('conditionsTreated');
      const res = await listClinicalData(selectedPatient?.value);
      if (res.medical_history) {
        setHistoryObjectId(res.medical_history.id || null);
        setConditionsTreated(res.medical_history.conditions_treated || null);
        setMedicalHistory(res.medical_history);
      }
      if (savedDraft) setConditionsTreated(savedDraft);
      setLoading(false);
      setHasUnsavedChanges(true);
    })();
  }, [selectedPatient]);

  if (loading) {
    return <Loading fullScreen />;
  }

  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.clinicalDataIntroContainer}`}>
        <PatientIntakeNavigation progress={28} />
        <form className={`${styles.formContainer}`}>
          <div className={styles.clinicalDataIntroWrapper}>
            <div>
              <div className={styles.clinicalDataIntroTitle}>
                <span>What conditions are being treated by your physician?</span>
                {!bypassRequired && <span className={styles.clinicalDataIntroTitleRequired}> *</span>}
              </div>
            </div>
            <div className={styles.clinicalDataIntroBody}>
              <div className={styles.formContent}>
                <TextField
                  hideLabel
                  multiline
                  minRows={4}
                  value={conditionsTreated || ''}
                  label="What conditions are being treated by your physician?"
                  errors={[]}
                  name="conditionsTreated"
                  onChange={(val: any) => {
                    userEditedRef.current = true;
                    setConditionsTreated(val.target.value);
                  }}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                />
              </div>
            </div>
            <div className={styles.clinicalDataIntroFooter}>
              <Button variant="text" className={styles.clinicalDataIntroFooterBack} onClick={() => navigate(-1)}>
                <ArrowBackIosNewIcon />
                Back
              </Button>
              <Button
                variant="contained"
                className={styles.clinicalDataIntroFooterNext}
                type="submit"
                disabled={loading || (!conditionsTreated && !bypassRequired)}
                onClick={(e) => {
                  e.preventDefault();
                  if (conditionsTreated || bypassRequired) {
                    onSubmit();
                  } else {
                    showSnackbar('Please fill in all required fields before proceeding.', 'error');
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default ClinicalDataStepThree;
